import { gameActionBus, type GameAction, type SkillSlot } from './actions';
import {getPreferences,resolveControlKey} from '../game/preferences';
import {installInputCancellation} from './inputCancellation';

const leftKeys = new Set(['a', 'arrowleft']);
const rightKeys = new Set(['d', 'arrowright']);
const upKeys = new Set(['w', 'arrowup']);
const downKeys = new Set(['s', 'arrowdown']);
const held = new Set<string>();
const shifts = new Set<string>();
let shortcutKeys = ['q','e','r','f'];
export function configureShortcutKeys(keys:string[]):void { shortcutKeys=[...keys]; }

/** Bind gameplay to physical positions; text fields retain normal layout input. */
export function physicalKey(event: Pick<KeyboardEvent, 'code' | 'key'>): string {
  if (/^Key[A-Z]$/.test(event.code)) return event.code.slice(3).toLowerCase();
  if (/^Digit[0-9]$/.test(event.code)) return event.code.slice(5);
  if (event.code === 'Space') return ' ';
  if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') return 'shift';
  if (event.code === 'ControlLeft' || event.code === 'ControlRight') return 'control';
  const punctuation:Record<string,string>={Minus:'-',Equal:'=',BracketLeft:'[',BracketRight:']',Backslash:'\\',Semicolon:';',Quote:"'",Comma:',',Period:'.',Slash:'/',Backquote:'`'};
  if(punctuation[event.code])return punctuation[event.code]!;
  if(event.code.startsWith('Arrow'))return event.code.toLowerCase();
  return event.key.toLowerCase();
}

export function actionForKey(keyValue: string): GameAction | null {
  const key = keyValue.toLowerCase();
  if (key === 'g') return { type: 'pickup' };
  if (key === 'j' || key === 'z') return { type: 'attack' };
  if (key === ' ' || key === 'c') return { type: 'jump' };
  if (key === 'control' || key === 'v') return { type: 'dodge' };
  if (key === 'shift') return { type: 'sprint', active: true };
  const slot=shortcutKeys.indexOf(key);
  if (slot>=0) return { type:'skill',slot:slot as SkillSlot };
  return null;
}

function currentAxis(): number {
  const left = [...leftKeys].some((key) => held.has(key));
  const right = [...rightKeys].some((key) => held.has(key));
  return (right ? 1 : 0) - (left ? 1 : 0);
}
function emitMotion(): void {
  gameActionBus.emit({ type: 'move', axis: currentAxis() });
  gameActionBus.emit({ type: 'move-depth', axis: Number([...downKeys].some(key => held.has(key))) - Number([...upKeys].some(key => held.has(key))) });
}

export function installKeyboardInput(target: Window = window): () => void {
  const onDown = (event: KeyboardEvent) => {
    if(typeof document!=='undefined'&&(document.body.classList.contains('rpg-modal')||document.body.classList.contains('world-travel-view')||document.body.classList.contains('story-battle-view')))return;
    if((event.ctrlKey&&physicalKey(event)!=='control')||event.metaKey||event.altKey)return;
    if (event.target instanceof Element && event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
    const key = resolveControlKey(event.code,physicalKey(event),getPreferences().keys);
    if(key==='shift')shifts.add(event.code||'shift');
    if (leftKeys.has(key) || rightKeys.has(key) || upKeys.has(key) || downKeys.has(key)) {
      if(event.repeat&&!held.has(key))return;
      held.add(key);
      emitMotion();
      event.preventDefault();
      return;
    }
    const action = actionForKey(key);
    if (action && !event.repeat) {
      gameActionBus.emit(action);
      event.preventDefault();
    }
  };
  const onUp = (event: KeyboardEvent) => {
    const key = resolveControlKey(event.code,physicalKey(event),getPreferences().keys);
    if(key==='shift'){shifts.delete(event.code||'shift');if(!shifts.size)gameActionBus.emit({type:'sprint',active:false});}
    if (leftKeys.has(key) || rightKeys.has(key) || upKeys.has(key) || downKeys.has(key)) {
      held.delete(key);
      emitMotion();
      event.preventDefault();
    }
  };
  const onBlur = () => {
    held.clear();
    shifts.clear();gameActionBus.emit({type:'sprint',active:false});
    emitMotion();
  };
  target.addEventListener('keydown', onDown);
  target.addEventListener('keyup', onUp);
  const cancel=installInputCancellation(onBlur,target,typeof document==='undefined'?undefined:document);
  return () => {
    target.removeEventListener('keydown', onDown);
    target.removeEventListener('keyup', onUp);
    cancel();
    held.clear();
    shifts.clear();gameActionBus.emit({type:'sprint',active:false});
  };
}
